import { CardArrivalNotice } from './components/CardArrivalNotice.js';
import { PendingResults } from './components/PendingResults.js';
import './interface-polish.css';
import { createPlatformAdapter } from '@blockbattle/platform-adapters';
import { gameSeasonAt } from '@blockbattle/shared';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { renewPlayerSession, api, ApiError } from './api.js';
import { resolveAppEntry } from './app-entry.js';
import { setAudioScene, unlockAudio } from './audio.js';
import { resolveAudioScene } from './audio-scene.js';
import { BottomNav } from './components/BottomNav.js';
import { DesktopHeader } from './components/DesktopHeader.js';
import { DiamondShortageDialog } from './components/DiamondShortageDialog.js';
import { GameTopUpDialog } from './components/GameTopUpDialog.js';
import { GlobalPlayerBar } from './components/GlobalPlayerBar.js';
import { ArenaThemeSync } from './components/ArenaThemeSync.js';
import { withPlatformInitializationTimeout } from './platform-initialization.js';
import { useAppStore } from './store.js';
import { consumeStartRoute } from './start-route.js';
import { consumeGameInviteReturn } from './game-invite-return.js';
import { createEmbeddedViewportController } from './viewport.js';
import { TUTORIAL_COMPLETE_KEY } from './tutorial-storage.js';

const TutorialScreen = lazy(() =>
  import('./screens/TutorialScreen.js').then(({ TutorialScreen: Screen }) => ({ default: Screen })),
);
const homeScreenModule = () => import('./screens/HomeScreen.js');
const HomeScreen = lazy(() =>
  homeScreenModule().then(({ HomeScreen: Screen }) => ({ default: Screen })),
);
const ChatScreen = lazy(() =>
  import('./screens/ChatScreen.js').then((m) => ({ default: m.ChatScreen })),
);
const CardsScreen = lazy(() =>
  import('./components/CardCollection.js').then((m) => ({ default: m.CardCollection })),
);
const PathScreen = lazy(() =>
  import('./screens/PathScreen.js').then(({ PathScreen: Screen }) => ({ default: Screen })),
);
const PathGameScreen = lazy(() =>
  import('./screens/PathGameScreen.js').then(({ PathGameScreen: Screen }) => ({ default: Screen })),
);
const GameScreen = lazy(() =>
  import('./screens/GameScreen.js').then(({ GameScreen: Screen }) => ({ default: Screen })),
);
const ResultScreen = lazy(() =>
  import('./screens/ResultScreen.js').then(({ ResultScreen: Screen }) => ({ default: Screen })),
);
const LeaderboardScreen = lazy(() =>
  import('./screens/LeaderboardScreen.js').then(({ LeaderboardScreen: Screen }) => ({
    default: Screen,
  })),
);
const ClubScreen = lazy(() =>
  import('./screens/ClubScreen.js').then(({ ClubScreen: Screen }) => ({ default: Screen })),
);
const ClubInviteScreen = lazy(() =>
  import('./screens/ClubInviteScreen.js').then(({ ClubInviteScreen: Screen }) => ({
    default: Screen,
  })),
);
const DuelScreen = lazy(() =>
  import('./screens/DuelScreen.js').then(({ DuelScreen: Screen }) => ({ default: Screen })),
);
const DuelTvScreen = lazy(() =>
  import('./screens/DuelTvScreen.js').then(({ DuelTvScreen: Screen }) => ({ default: Screen })),
);
const DuelsScreen = lazy(() =>
  import('./screens/DuelsScreen.js').then(({ DuelsScreen: Screen }) => ({ default: Screen })),
);
const ProfileScreen = lazy(() =>
  import('./screens/ProfileScreen.js').then(({ ProfileScreen: Screen }) => ({ default: Screen })),
);
const StoreScreen = lazy(() =>
  import('./screens/StoreScreen.js').then(({ StoreScreen: Screen }) => ({ default: Screen })),
);
const DocsScreen = lazy(() =>
  import('./screens/DocsScreen.js').then(({ DocsScreen: Screen }) => ({ default: Screen })),
);
const LandingScreen = lazy(() =>
  import('./screens/LandingScreen.js').then(({ LandingScreen: Screen }) => ({ default: Screen })),
);
const PublicDocumentScreen = lazy(() =>
  import('./screens/LandingScreen.js').then(({ PublicDocumentScreen: Screen }) => ({
    default: Screen,
  })),
);
const PaymentReviewScreen = lazy(() =>
  import('./screens/PaymentReviewScreen.js').then(({ PaymentReviewScreen: Screen }) => ({
    default: Screen,
  })),
);

const pathFallback = (
  <main className="path-game-state">
    <div className="loader" aria-label="Загрузка Пути" />
    <p>Готовим Путь мастера…</p>
  </main>
);

const routeFallback = (
  <main className="game-state-screen centered" aria-live="polite">
    <div className="loader" aria-label="Загрузка экрана" />
    <p>Загружаем…</p>
  </main>
);

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const forcePublicPreview =
    isLocalhost && new URLSearchParams(window.location.search).get('landing') === '1';
  const appEntry = resolveAppEntry(window.location.pathname);
  const isPaymentReview = window.location.pathname.startsWith('/payment-review/');
  const isPublicWebsite = forcePublicPreview || isPaymentReview || (!isLocalhost && !appEntry);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [initializationError, setInitializationError] = useState<{
    code: string;
    message: string;
  } | null>(null);
  const [retry, setRetry] = useState(0);
  const initialPathname = useRef(location.pathname);
  const initialization = useRef<Promise<{
    adapter: ReturnType<typeof createPlatformAdapter>;
    session: Awaited<ReturnType<typeof api.authenticate>>;
    startParameter: string | null;
    rawLaunchData: string;
  }> | null>(null);
  const setAdapter = useAppStore((state) => state.setAdapter);
  const setSession = useAppStore((state) => state.setSession);
  const adapter = useAppStore((state) => state.adapter);
  const reducedMotion = useAppStore((state) => state.reducedMotion);
  useEffect(() => {
    document.documentElement.dataset['season'] = gameSeasonAt().theme;
  }, []);

  useEffect(() => {
    if (!isPublicWebsite) setAudioScene(resolveAudioScene(location.pathname));
  }, [isPublicWebsite, location.pathname]);

  useEffect(() => {
    if (isPublicWebsite) return;
    // Embedded clients keep the same document alive between routes. Reset both supported
    // scroll roots: VK owns an in-frame shell scroller, while MAX/web use the document.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.querySelector<HTMLElement>('.app-shell')?.scrollTo({ top: 0, left: 0 });
  }, [isPublicWebsite, location.pathname]);

  useEffect(() => {
    document.documentElement.dataset['reducedMotion'] = reducedMotion ? 'true' : 'false';
  }, [reducedMotion]);

  useEffect(() => {
    const unlock = (): void => void unlockAudio();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  useEffect(() => {
    if (isPublicWebsite) return;
    let animationFrame = 0;
    const viewportController = createEmbeddedViewportController(
      () => ({
        width: window.visualViewport?.width ?? window.innerWidth,
        height: window.visualViewport?.height ?? window.innerHeight,
      }),
      () => adapter?.getViewport() ?? Promise.resolve(undefined),
      ({ width, height }) => {
        document.documentElement.style.setProperty('--app-viewport-width', `${width}px`);
        document.documentElement.style.setProperty('--app-viewport-height', `${height}px`);
        window.dispatchEvent(new Event('bb:viewport-layout'));
      },
    );
    const updateOffset = (): void => {
      const root = document.documentElement;
      const top = `${window.visualViewport?.offsetTop ?? 0}px`;
      if (root.style.getPropertyValue('--app-viewport-top') !== top)
        root.style.setProperty('--app-viewport-top', top);
    };
    const resume = (): void => {
      if (!document.hidden) void viewportController.update(true);
    };
    const scheduleUpdate = (): void => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        updateOffset();
        void viewportController.update();
      });
    };
    updateOffset();
    void viewportController.update();
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('resize', scheduleUpdate);
    window.visualViewport?.addEventListener('resize', scheduleUpdate);
    // Older WebViews can report a keyboard resize as scroll. The controller only
    // requests native bounds when dimensions changed; offset-only panning is cheap.
    window.visualViewport?.addEventListener('scroll', scheduleUpdate);
    return () => {
      viewportController.dispose();
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', scheduleUpdate);
      window.visualViewport?.removeEventListener('resize', scheduleUpdate);
      window.visualViewport?.removeEventListener('scroll', scheduleUpdate);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [adapter, isPublicWebsite]);

  useEffect(() => {
    if (isPublicWebsite) return;
    let cancelled = false;
    const initialize = async (): Promise<void> => {
      try {
        initialization.current ??= (async () => {
          const adapter = createPlatformAdapter(appEntry?.platform);
          document.documentElement.dataset['platform'] = adapter.platform;
          const [launch, user] = await withPlatformInitializationTimeout(
            (async () => {
              await adapter.initialize();
              return Promise.all([adapter.getLaunchData(), adapter.getCurrentUser()]);
            })(),
          );
          const referralCode = launch.startParameter?.startsWith('ref_')
            ? launch.startParameter.slice(4)
            : undefined;
          const authBody =
            adapter.platform === 'web'
              ? {
                  platform: 'web',
                  externalId: user.id,
                  displayName: `${user.firstName} ${user.lastName ?? ''}`.trim(),
                  ...(referralCode ? { referralCode } : {}),
                }
              : adapter.platform === 'max'
                ? {
                    platform: 'max',
                    launchData: launch.raw,
                    ...(referralCode ? { referralCode } : {}),
                  }
                : {
                    platform: 'vk',
                    launchData: launch.raw,
                    displayName: `${user.firstName} ${user.lastName ?? ''}`.trim(),
                    ...(referralCode ? { referralCode } : {}),
                  };
          let session: Awaited<ReturnType<typeof api.authenticate>>;
          try {
            session = await api.authenticate(authBody);
          } catch (error) {
            // MAX launch signatures expire sooner than a long game. Only resume the same launch/user.
            const cached = sessionStorage.getItem('bb_auth_session');
            if (!cached || sessionStorage.getItem('bb_auth_launch') !== launch.raw) throw error;
            const previous = JSON.parse(cached) as Awaited<ReturnType<typeof api.authenticate>>;
            if (previous.user.id !== user.id || previous.user.platform !== adapter.platform)
              throw error;
            await renewPlayerSession();
            session = { ...previous, accessToken: sessionStorage.getItem('bb_access_token')! };
          }
          sessionStorage.setItem('bb_session_owner', session.user.internalId);
          if (session.refreshToken)
            sessionStorage.setItem('bb_refresh_token', session.refreshToken);
          else sessionStorage.removeItem('bb_refresh_token');
          sessionStorage.setItem('bb_auth_session', JSON.stringify(session));
          sessionStorage.setItem('bb_auth_launch', launch.raw);
          return {
            adapter,
            session,
            startParameter: launch.startParameter,
            rawLaunchData: launch.raw,
          };
        })();
        const { adapter, session, startParameter, rawLaunchData } = await initialization.current;
        sessionStorage.setItem('bb_access_token', session.accessToken);
        let effectiveSession = session;
        const legacyTutorialComplete = localStorage.getItem(TUTORIAL_COMPLETE_KEY) === 'done';
        if (!session.user.tutorialComplete && legacyTutorialComplete) {
          effectiveSession = {
            ...session,
            user: { ...session.user, tutorialComplete: true },
          };
          void api.updateTutorial(true).catch(() => undefined);
        }
        if (effectiveSession.user.tutorialComplete)
          localStorage.setItem(TUTORIAL_COMPLETE_KEY, 'done');
        if (!cancelled) {
          const startRoute = consumeStartRoute(
            adapter.platform,
            rawLaunchData,
            startParameter,
            sessionStorage,
          );
          const inviteReturn =
            !startRoute && initialPathname.current === '/' && session.user.internalId
              ? consumeGameInviteReturn(session.user.internalId)
              : null;
          if (
            !startRoute &&
            !inviteReturn &&
            effectiveSession.user.tutorialComplete &&
            initialPathname.current === '/'
          )
            await withPlatformInitializationTimeout(homeScreenModule());
          if (startRoute) void navigate(startRoute, { replace: true });
          else if (inviteReturn) void navigate(inviteReturn, { replace: true });
          else if (!effectiveSession.user.tutorialComplete && initialPathname.current === '/')
            void navigate('/tutorial', { replace: true });
          setAdapter(adapter);
          setSession(effectiveSession);
          setInitializationError(null);
          setStatus('ready');
        }
      } catch (error) {
        if (!cancelled) {
          initialization.current = null;
          const apiError = error instanceof ApiError ? error : null;
          const bridgeMessage = error instanceof Error ? error.message : '';
          const launchPlatform = appEntry?.platform;
          setInitializationError({
            code: apiError?.code ?? 'PLATFORM_INIT_ERROR',
            message:
              apiError?.message ??
              (bridgeMessage.includes('TIMEOUT')
                ? 'Загрузка заняла слишком много времени. Проверьте соединение и повторите попытку.'
                : launchPlatform === 'vk' || bridgeMessage.includes('VK')
                  ? 'VK Bridge не передал данные запуска. Откройте приложение из каталога VK.'
                  : launchPlatform === 'max' || bridgeMessage.includes('MAX')
                    ? 'MAX не передал данные запуска. Откройте мини-приложение из бота.'
                    : 'Не удалось получить данные платформы. Откройте приложение заново.'),
          });
          setStatus('error');
        }
      }
    };
    void initialize();
    return () => {
      cancelled = true;
    };
  }, [appEntry?.platform, isPublicWebsite, navigate, retry, setAdapter, setSession]);

  if (isPublicWebsite)
    return (
      <Suspense fallback={routeFallback}>
        <Routes>
          <Route path="/" element={<LandingScreen />} />
          <Route path="/app" element={<LandingScreen />} />
          <Route path="/shop" element={<LandingScreen />} />
          <Route path="/docs/:page" element={<PublicDocumentScreen />} />
          <Route path="/payment-review/:token" element={<PaymentReviewScreen />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );

  if (status === 'loading')
    return (
      <div className="splash">
        <div className="splash-card">
          <Logo />
          <div className="loader" aria-label="Загрузка" />
          <p>Готовим арену…</p>
        </div>
      </div>
    );
  if (status === 'error')
    return (
      <div className="splash">
        <div className="splash-card">
          <Logo />
          <h1>Не удалось войти в игру</h1>
          <p>{initializationError?.message ?? 'Не удалось выполнить вход.'}</p>
          <button
            className="primary-button"
            onClick={() => {
              initialization.current = null;
              setStatus('loading');
              setRetry((value) => value + 1);
            }}
          >
            Повторить
          </button>
        </div>
      </div>
    );
  return (
    <div
      className={`app-shell ${
        location.pathname.startsWith('/game/') ||
        location.pathname.startsWith('/path/game/') ||
        location.pathname === '/tutorial'
          ? 'fullscreen'
          : ''
      }`}
    >
      <PendingResults />
      <ArenaThemeSync />
      <DesktopHeader />
      <GlobalPlayerBar />
      <CardArrivalNotice />
      <Suspense fallback={routeFallback}>
        <Routes>
          <Route path="/" element={<HomeScreen />} />
          <Route path="/chat" element={<ChatScreen />} />
          <Route path="/cards" element={<CardsScreen />} />
          <Route
            path="/tutorial"
            element={
              <Suspense
                fallback={
                  <main className="game-layout game-state-screen centered">
                    <div className="loader" aria-label="Загрузка обучения" />
                    <p>Готовим первое поле…</p>
                  </main>
                }
              >
                <TutorialScreen />
              </Suspense>
            }
          />
          <Route
            path="/game/:mode"
            element={<GameScreen key={`${location.pathname}${location.search}`} />}
          />
          <Route path="/result" element={<ResultScreen />} />
          <Route path="/leaders" element={<LeaderboardScreen />} />
          <Route
            path="/path"
            element={
              <Suspense fallback={pathFallback}>
                <PathScreen />
              </Suspense>
            }
          />
          <Route
            path="/path/game/:level"
            element={
              <Suspense fallback={pathFallback}>
                <PathGameScreen key={location.pathname} />
              </Suspense>
            }
          />
          <Route path="/club/invite/:code" element={<ClubInviteScreen />} />
          <Route path="/club/:id?" element={<ClubScreen />} />
          <Route path="/duel/:code/tv" element={<DuelTvScreen />} />
          <Route path="/duel/:code" element={<DuelScreen />} />
          <Route path="/duels" element={<DuelsScreen />} />
          <Route path="/profile" element={<ProfileScreen />} />
          <Route path="/profile/:id" element={<ProfileScreen />} />
          <Route path="/store" element={<StoreScreen />} />
          <Route path="/docs/:page" element={<DocsScreen />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <BottomNav />
      <DiamondShortageDialog />
      <GameTopUpDialog />
    </div>
  );
}

function Logo() {
  return (
    <div className="brand">
      <span className="brand-mark">
        <i />
        <i />
        <i />
        <i />
      </span>
      <span>
        Блок<span>Битва</span>
      </span>
    </div>
  );
}
